/**
 * `Button` — bottone riusabile del design system.
 *
 * Wrappa `<button>` (che eredita gli stili base globali) e aggiunge varianti
 * (`primary`, `danger`, `ghost`), taglia `sm`, larghezza piena (`block`) e uno
 * stato `loading` che mostra uno `Spinner` e disabilita l'interazione.
 *
 * Non altera ruoli né semantica: resta un `<button type=...>` nativo, così i
 * test e l'accessibilità esistenti continuano a funzionare.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Spinner } from './Spinner';

export type ButtonVariant = 'default' | 'primary' | 'danger' | 'ghost';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: 'md' | 'sm';
  block?: boolean;
  loading?: boolean;
  /**
   * Rende il pulsante come controllo quadrato solo‑icona (senza etichetta
   * visibile). In questo caso fornire sempre un `aria-label` per l'accessibilità.
   */
  iconOnly?: boolean;
  children: ReactNode;
}

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  default: '',
  primary: 'ui-btn--primary',
  danger: 'ui-btn--danger',
  ghost: 'ui-btn--ghost',
};

export function Button({
  variant = 'default',
  size = 'md',
  block = false,
  loading = false,
  iconOnly = false,
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const classes = [
    'ui-btn',
    VARIANT_CLASS[variant],
    size === 'sm' ? 'ui-btn--sm' : '',
    block ? 'ui-btn--block' : '',
    iconOnly ? 'ui-btn--icon' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  // Su un pulsante solo‑icona lo spinner SOSTITUISCE l'icona (niente due glifi
  // affiancati né variazioni di larghezza); altrimenti lo affianca all'etichetta.
  const content = loading ? (
    iconOnly ? (
      <Spinner size="sm" />
    ) : (
      <>
        <Spinner size="sm" />
        {children}
      </>
    )
  ) : (
    children
  );

  return (
    <button
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {content}
    </button>
  );
}
